import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { superApi, formatMxn } from '../../lib/superApi';
import { Calendar, CreditCard, Landmark, Loader2, Check, X } from 'lucide-react';

const STATUS_LABELS = {
  pending: 'Pendiente',
  paid: 'Pagado',
  rejected: 'Rechazado',
};

function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const inputClass =
  'mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500';
const btnPrimary =
  'inline-flex items-center gap-2 rounded-md bg-teal-600 hover:bg-teal-500 text-white text-sm font-medium px-4 py-2 disabled:opacity-50';
const btnOutline =
  'inline-flex items-center gap-2 rounded-md border border-slate-600 text-slate-200 hover:bg-slate-800 text-sm px-4 py-2 disabled:opacity-50';
const card = 'rounded-lg border border-slate-800 bg-slate-900/60 p-5 space-y-4';

export default function SuperBilling() {
  const [platform, setPlatform] = useState(null);
  const [payments, setPayments] = useState([]);
  const [trialStart, setTrialStart] = useState('');
  const [trialEnd, setTrialEnd] = useState('');
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [periodStart, setPeriodStart] = useState('');
  const [monthlyAmount, setMonthlyAmount] = useState('0');
  const [annualAmount, setAnnualAmount] = useState('0');
  const [mpPublicKey, setMpPublicKey] = useState('');
  const [mpAccessToken, setMpAccessToken] = useState('');
  const [transferInstructions, setTransferInstructions] = useState('');
  const [warningDays, setWarningDays] = useState('3');
  const [saving, setSaving] = useState('');

  const load = async () => {
    const [platRes, payRes] = await Promise.all([
      superApi.get('/super/platform'),
      superApi.get('/super/platform/subscription/payments'),
    ]);
    const p = platRes.data;
    setPlatform(p);
    setPayments(payRes.data || []);
    setTrialStart(p.trial_start_date || '');
    setTrialEnd(p.trial_end_date || '');
    setBillingInterval(p.billing_interval || 'monthly');
    setMonthlyAmount(String(p.monthly_amount ?? 0));
    setAnnualAmount(String(p.annual_amount ?? 0));
    setMpPublicKey(p.platform_mp_public_key || '');
    setMpAccessToken(p.platform_mp_access_token || '');
    setTransferInstructions(p.transfer_instructions || '');
    setWarningDays(String(p.warning_days ?? 3));
    const started = (p.subscription_started_at || '').slice(0, 10);
    setPeriodStart(started || todayIso());
  };

  useEffect(() => {
    load().catch(() => toast.error('No se pudo cargar suscripción'));
  }, []);

  const saveTrial = async () => {
    setSaving('trial');
    try {
      const { data } = await superApi.put('/super/platform/subscription/trial', {
        trial_start_date: trialStart,
        trial_end_date: trialEnd,
      });
      setPlatform(data);
      toast.success('Periodo de prueba actualizado');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar fechas');
    } finally {
      setSaving('');
    }
  };

  const activateSubscription = async () => {
    setSaving('activate');
    try {
      const { data } = await superApi.put('/super/platform/subscription/activate', {
        billing_interval: billingInterval,
        monthly_amount: parseFloat(monthlyAmount) || 0,
        annual_amount: parseFloat(annualAmount) || 0,
        period_start_date: periodStart || undefined,
      });
      setPlatform(data);
      toast.success('Suscripción activada');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al activar');
    } finally {
      setSaving('');
    }
  };

  const suspendSubscription = async () => {
    setSaving('suspend');
    try {
      const { data } = await superApi.put('/super/platform/subscription/suspend');
      setPlatform(data);
      toast.success('Suscripción suspendida');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al suspender');
    } finally {
      setSaving('');
    }
  };

  const reactivateSubscription = async () => {
    setSaving('reactivate');
    try {
      const { data } = await superApi.put('/super/platform/subscription/reactivate');
      setPlatform(data);
      toast.success('Suscripción reactivada');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al reactivar');
    } finally {
      setSaving('');
    }
  };

  const saveBillingConfig = async () => {
    setSaving('billing');
    try {
      const wd = Math.max(1, Math.min(30, parseInt(warningDays, 10) || 3));
      await superApi.put('/super/platform/billing-config', {
        platform_mp_public_key: mpPublicKey,
        platform_mp_access_token: mpAccessToken,
        transfer_instructions: transferInstructions,
        warning_days: wd,
      });
      toast.success('Configuración de cobro guardada');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar cobro');
    } finally {
      setSaving('');
    }
  };

  const confirmPayment = async (id) => {
    try {
      await superApi.put(`/super/platform/subscription/payments/${id}/confirm`);
      toast.success('Transferencia confirmada');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al confirmar');
    }
  };

  const rejectPayment = async (id) => {
    try {
      await superApi.put(`/super/platform/subscription/payments/${id}/reject`);
      toast.success('Transferencia rechazada');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al rechazar');
    }
  };

  if (!platform) {
    return <div className="text-slate-400">Cargando…</div>;
  }

  const phaseLabel =
    {
      trial: 'Periodo de prueba',
      active: 'Suscripción activa',
      suspended: 'Suspendida',
    }[platform.subscription_phase] || platform.subscription_phase;

  const isSuspended = platform.subscription_phase === 'suspended';

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-teal-400">DanySolutions</div>
        <h1 className="text-3xl font-semibold text-white mt-1">Suscripción y cobros</h1>
        <p className="text-slate-400 text-sm mt-2">
          Periodo de prueba, activación, Mercado Pago de plataforma y transferencias.
        </p>
      </div>

      <div className={card}>
        <div className="text-sm text-slate-300">
          Estado: <span className="text-white font-medium">{phaseLabel}</span>
          {platform.current_period_end && (
            <span className="ml-3 text-slate-400">· Vence: {platform.current_period_end}</span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {isSuspended ? (
            <button type="button" onClick={reactivateSubscription} disabled={saving === 'reactivate'} className={btnPrimary}>
              {saving === 'reactivate' ? <Loader2 className="animate-spin" size={14} /> : null}
              Reactivar suscripción
            </button>
          ) : (
            <button
              type="button"
              onClick={suspendSubscription}
              disabled={saving === 'suspend' || platform.subscription_phase === 'trial'}
              className={btnOutline}
            >
              {saving === 'suspend' ? <Loader2 className="animate-spin" size={14} /> : null}
              Suspender suscripción
            </button>
          )}
        </div>
        <p className="text-xs text-slate-500">
          Suspender bloquea el panel admin. No afecta citas ni datos clínicos. Reactivar no cambia la fecha de vencimiento.
        </p>
      </div>

      <section className={card}>
        <div className="flex items-center gap-2 text-white">
          <Calendar size={18} className="text-teal-400" />
          <h2 className="text-xl font-medium">Periodo de prueba</h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block text-xs text-slate-400">
            Inicio
            <input type="date" className={inputClass} value={trialStart} onChange={(e) => setTrialStart(e.target.value)} />
          </label>
          <label className="block text-xs text-slate-400">
            Fin
            <input type="date" className={inputClass} value={trialEnd} onChange={(e) => setTrialEnd(e.target.value)} />
          </label>
        </div>
        <button type="button" onClick={saveTrial} disabled={saving === 'trial'} className={btnPrimary}>
          {saving === 'trial' ? <Loader2 className="animate-spin" size={14} /> : null}
          Guardar fechas de prueba
        </button>
      </section>

      <section className={card}>
        <h2 className="text-xl font-medium text-white">Activar suscripción</h2>
        <p className="text-xs text-slate-500">
          Pasa de prueba a suscripción pagada. Define intervalo, inicio del periodo y montos en MXN.
        </p>
        <div className="flex flex-wrap gap-4 text-sm text-slate-300">
          <label className="flex items-center gap-2">
            <input type="radio" checked={billingInterval === 'monthly'} onChange={() => setBillingInterval('monthly')} />
            Mensual
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" checked={billingInterval === 'annual'} onChange={() => setBillingInterval('annual')} />
            Anual
          </label>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <label className="block text-xs text-slate-400">
            Inicio del periodo
            <input type="date" className={inputClass} value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
          </label>
          <label className="block text-xs text-slate-400">
            Monto mensual (MXN)
            <input type="number" min="0" step="0.01" className={inputClass} value={monthlyAmount} onChange={(e) => setMonthlyAmount(e.target.value)} />
          </label>
          <label className="block text-xs text-slate-400">
            Monto anual (MXN)
            <input type="number" min="0" step="0.01" className={inputClass} value={annualAmount} onChange={(e) => setAnnualAmount(e.target.value)} />
          </label>
        </div>
        <button type="button" onClick={activateSubscription} disabled={saving === 'activate'} className={btnPrimary}>
          {saving === 'activate' ? <Loader2 className="animate-spin" size={14} /> : null}
          Suscripción comenzada
        </button>
      </section>

      <section className={card}>
        <div className="flex items-center gap-2 text-white">
          <CreditCard size={18} className="text-teal-400" />
          <h2 className="text-xl font-medium">Mercado Pago (DanySolutions)</h2>
        </div>
        <label className="block text-xs text-slate-400">
          Public key
          <input className={`${inputClass} font-mono text-xs`} value={mpPublicKey} onChange={(e) => setMpPublicKey(e.target.value)} />
        </label>
        <label className="block text-xs text-slate-400">
          Access token
          <input type="password" className={`${inputClass} font-mono text-xs`} value={mpAccessToken} onChange={(e) => setMpAccessToken(e.target.value)} />
        </label>
        <div className="flex items-center gap-2 text-white pt-2">
          <Landmark size={18} className="text-teal-400" />
          <h3 className="text-lg font-medium">Transferencia bancaria</h3>
        </div>
        <textarea
          className={`${inputClass} min-h-[120px]`}
          placeholder="Banco, CLABE, titular, referencia…"
          value={transferInstructions}
          onChange={(e) => setTransferInstructions(e.target.value)}
        />
        <label className="block text-xs text-slate-400">
          Recordatorio N días antes
          <input
            type="number"
            min={1}
            max={30}
            className={`${inputClass} max-w-[140px]`}
            value={warningDays}
            onChange={(e) => setWarningDays(e.target.value)}
          />
          <span className="block text-slate-500 mt-1">Banner de aviso en el panel admin (1–30).</span>
        </label>
        <button type="button" onClick={saveBillingConfig} disabled={saving === 'billing'} className={btnPrimary}>
          {saving === 'billing' ? <Loader2 className="animate-spin" size={14} /> : null}
          Guardar configuración de cobro
        </button>
      </section>

      <section className={card}>
        <h2 className="text-xl font-medium text-white">Historial de pagos</h2>
        {payments.length === 0 ? (
          <p className="text-sm text-slate-500">Sin pagos registrados.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="text-slate-500 text-xs border-b border-slate-800">
                  <th className="py-2 pr-3">Fecha</th>
                  <th className="py-2 pr-3">Monto</th>
                  <th className="py-2 pr-3">Método</th>
                  <th className="py-2 pr-3">Estado</th>
                  <th className="py-2">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-b border-slate-800/60 text-slate-300">
                    <td className="py-2 pr-3 text-xs">{(p.created_at || '').slice(0, 10)}</td>
                    <td className="py-2 pr-3">{formatMxn(p.amount)}</td>
                    <td className="py-2 pr-3 capitalize">
                      {p.method === 'mercadopago' ? 'Mercado Pago' : 'Transferencia'}
                    </td>
                    <td className="py-2 pr-3">{STATUS_LABELS[p.status] || p.status}</td>
                    <td className="py-2">
                      {p.method === 'transfer' && p.status === 'pending' && (
                        <div className="flex gap-2">
                          <button type="button" onClick={() => confirmPayment(p.id)} className="text-green-400 hover:text-green-300" title="Confirmar">
                            <Check size={16} />
                          </button>
                          <button type="button" onClick={() => rejectPayment(p.id)} className="text-red-400 hover:text-red-300" title="Rechazar">
                            <X size={16} />
                          </button>
                        </div>
                      )}
                      {p.proof_note && (
                        <div className="text-[10px] text-slate-500 mt-1 max-w-[200px] truncate" title={p.proof_note}>
                          {p.proof_note}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
